import { useEffect, useState } from 'react'
import { match as MatchProps, useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Icon } from '@iconify/react'
import {
  StringParam,
  useQueryParams,
  withDefault,
  JsonParam,
  NumberParam,
  BooleanParam,
} from 'use-query-params'
import {
  TabPane,
  Tabs,
  useGetProjectDomain,
  useGlobalState,
  useProjectDetails,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useOrderItems } from 'utils/hooks/useOrderItems'
import { useDocItems } from 'utils/hooks/useDocItems'
import ProjectDropdown from 'components/ProjectDropdown/ProjectDropdown'
import DomainPill from 'components/DomainPill'

import Items from './Items/Items'
import IdeaLab from './IdeaLab/IdeaLab'
import LinkDomainModal from './IdeaLab/LinkDomainModal'

import s from './Project.module.css'

type Props = {
  match: MatchProps<{ projectId: string; tab?: string }>
}

const Project = ({ match }: Props) => {
  const companyId = useSelectedCompanyId()
  const { projectId, tab } = match.params
  const { setTopbarComponent, permissions, subscription } = useGlobalState()
  const [queryParams, setQueryParams] = useQueryParams({
    search_title: withDefault(StringParam, undefined),
    filters: withDefault(JsonParam, undefined),
    link_domain: withDefault(BooleanParam, undefined),
    sort: withDefault(JsonParam, undefined),
    currentPage: withDefault(NumberParam, undefined),
    tab: withDefault(StringParam, undefined),
    item_types: withDefault(StringParam, undefined),
  })

  const { data: project, refetch: refetchProjectDetails } = useProjectDetails(
    projectId,
    {
      company_id: companyId,
    }
  )

  const history = useHistory()

  const { total: totalOrders } = useOrderItems({
    companyId,
    projectId,
    pageSize: 1,
    options: {
      isEnabled: true,
    },
  })
  const { total: totalDocs } = useDocItems({
    companyId,
    projectId,
    pageSize: 1,
    options: {
      isEnabled: true,
    },
  })

  const isMarketplaceEnabled = permissions?.marketplace

  const showOrdersTab = !!isMarketplaceEnabled || !!totalOrders

  const getDefaultActiveTab = () => {
    if (subscription?.key === 'pt_grandfathered') {
      return 'DOCS'
    }

    return showOrdersTab ? 'ORDERS' : 'LAB'
  }

  const [activeKey, setActiveKey] = useState(getDefaultActiveTab())

  const { data: projectDomain, isLoading: isLoadingProjectDomain } =
    useGetProjectDomain({
      data: {
        companyId,
        projectId,
      },
    })

  const handleTabChange = (_tabKey: string) => {
    setActiveKey(_tabKey)
    const isOrderOrDocs = _tabKey === 'ORDERS' || _tabKey === 'DOCS'
    const itemType =
      _tabKey === 'ORDERS' ? 'item_types=assignment' : 'item_types=private_doc'
    const search = isOrderOrDocs ? itemType : undefined
    history.push({
      pathname: `/project/${projectId}/${isOrderOrDocs ? 'items' : 'topics'}`,
      search,
    })
  }

  const domain = projectDomain?.domain

  const capitalisedTab = tab && tab.charAt(0).toUpperCase() + tab.slice(1)

  const metaTitle =
    capitalisedTab && project?.project_title
      ? `${capitalisedTab} in ${project.project_title}`
      : 'Project'

  const showDraft = queryParams.tab === 'draft'

  useEffect(() => {
    const itemsActiveKey =
      queryParams.item_types === 'private_doc' ? 'DOCS' : 'ORDERS'
    setActiveKey(tab === 'items' ? itemsActiveKey : 'LAB')
  }, [tab])

  useEffect(() => {
    if (project) {
      setTopbarComponent(
        <div className={s.projectHeader}>
          <div className={s.titleWrapper}>
            <div className={s.projectTitle}>
              <span className={s.title}>{project?.project_title ?? ''}</span>
              {project?.id && (
                <span className={s.projectCode}>#{project.project_code}</span>
              )}
            </div>
            <ProjectDropdown
              icon="chevron"
              projectId={project.id}
              projectName={project.project_title}
              projectStatus={project.status}
              refetch={refetchProjectDetails}
            />
          </div>
          {domain && (
            <>
              <div className={s.dot}>•</div>
              <div className={s.domain}>
                <DomainPill domain={domain} />
              </div>
            </>
          )}
        </div>
      )
    }
    return () => {
      setTopbarComponent(null)
    }
  }, [project, refetchProjectDetails, domain])

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
      </Helmet>
      <div className={s.wrapper}>
        {showDraft ? (
          <Items match={match} project={project} />
        ) : (
          <Tabs
            onTabClick={handleTabChange}
            activeKey={activeKey}
            defaultActiveKey={activeKey}
            destroyInactiveTabPane={true}
          >
            <TabPane
              key="LAB"
              tabKey="LAB"
              tab={
                <div className={s.tabPane}>
                  <Icon
                    icon="heroicons:sparkles-solid"
                    width={18}
                    height={18}
                    className={s.icon}
                  />
                  Idea Lab
                </div>
              }
            >
              <IdeaLab
                domain={domain}
                projectId={projectId}
                companyId={companyId}
                isLoadingProjectDomain={isLoadingProjectDomain}
              />
            </TabPane>

            {showOrdersTab && (
              <TabPane
                key="ORDERS"
                tabKey="ORDERS"
                tab={<div className={s.tabPane}>Orders ({totalOrders})</div>}
              >
                <Items match={match} project={project} />
              </TabPane>
            )}
            <TabPane
              key="DOCS"
              tabKey="DOCS"
              tab={<div className={s.tabPane}>Internal Docs ({totalDocs})</div>}
            >
              <Items match={match} project={project} />
            </TabPane>
          </Tabs>
        )}
      </div>
      {!domain && (
        <LinkDomainModal
          isOpen={!!queryParams.link_domain}
          onClose={() => setQueryParams({ link_domain: undefined })}
        />
      )}
    </>
  )
}

export default Project
